/* ==========================================================================
   TK GOURMET — GUIDE EXPERIENCE LAYER
   Horizon Development 1.4 · assets/tkg-guides.js

   Progressive enhancement for the `.tkg` guide pages. Everything degrades to
   the plain stacked layout if this file never runs.

   1.  Sharper hero artwork at full-bleed widths
   2.  Moves the page <h1> into the hero so it reads as an article opener
   3.  Wraps the sections in a rail + reading-column grid
   4.  Builds a sticky table of contents from the jump chips (or the headings)
   5.  Scroll spy, reading progress, back to top
   6.  Smooth anchor jumps that clear a sticky header when there is one
   7.  Reveal-on-scroll for cards, tiles, tables and meters
   ========================================================================== */

(function () {
  'use strict';

  var doc = document;
  var root = doc.querySelector('.tkg');
  if (!root) return;

  // Declared up here on purpose: this script is deferred, so document.readyState
  // is already "interactive" and init() runs synchronously below — before any
  // assignment further down the file would have happened.
  var handlers = [];
  var ticking = false;

  // On desktop Horizon scrolls `.page-wrapper` (html and body are
  // overflow:hidden); on narrow viewports the document scrolls normally. The
  // helpers below read and write scroll through whichever one is live, and
  // attachScrollHost() re-points the listener if a resize swaps them.
  var scroller = null;
  var scrollHost = null;

  function findScroller() {
    var el = doc.querySelector('.page-wrapper');
    if (!el) return null;
    var oy = window.getComputedStyle(el).overflowY;
    return oy === 'auto' || oy === 'scroll' ? el : null;
  }

  function tick() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      handlers.forEach(function (h) {
        h();
      });
      ticking = false;
    });
  }

  function attachScrollHost() {
    var next = findScroller() || window;
    if (next === scrollHost) return;
    if (scrollHost) scrollHost.removeEventListener('scroll', tick);
    scrollHost = next;
    scroller = scrollHost === window ? null : scrollHost;
    scrollHost.addEventListener('scroll', tick, { passive: true });
    applySmoothScroll();
  }

  function scrollTop() {
    return scroller ? scroller.scrollTop : window.pageYOffset || doc.documentElement.scrollTop || 0;
  }

  function viewportH() {
    return scroller ? scroller.clientHeight : window.innerHeight;
  }

  function contentH() {
    return scroller ? scroller.scrollHeight : doc.documentElement.scrollHeight;
  }

  // Distance from the top of the scrolling content to an element.
  function offsetTopIn(el) {
    var base = scroller ? scroller.getBoundingClientRect().top : 0;
    return el.getBoundingClientRect().top - base + scrollTop();
  }

  function ready(fn) {
    if (doc.readyState === 'loading') {
      doc.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  ready(init);

  function init() {
    root.classList.add('tkg-js');
    attachScrollHost();

    var hero = root.querySelector('.tkg-hero');

    upgradeHeroArt(hero);
    relocateHeading(hero);

    var shell = buildShell(hero);
    var links = buildRail(shell.rail);
    if (!links.length) {
      // Nothing to put in the rail: collapse it so the column isn't left empty.
      shell.body.classList.add('tkg-norail');
      shell.rail.style.display = 'none';
    }

    setOffset();
    applySmoothScroll();
    wireSpy(links);
    wireProgress();
    wireBackToTop();
    wireReveal();

    window.addEventListener(
      'resize',
      debounce(function () {
        attachScrollHost();
        setOffset();
        tick();
      }, 150)
    );
  }

  /* ---------------------------------------------------------------- 1 */
  function upgradeHeroArt(hero) {
    if (!hero) return;
    var bg = hero.style.backgroundImage;
    if (bg && bg.indexOf('width=') > -1) {
      hero.style.backgroundImage = bg.replace(/width=\d+/, 'width=2400');
    }
  }

  /* ---------------------------------------------------------------- 2 */
  function relocateHeading(hero) {
    if (!hero) return;
    var h1 = doc.querySelector('#MainContent h1');
    var inner = hero.firstElementChild;
    if (!h1 || !inner || hero.contains(h1)) return;

    var holder = h1.parentElement;
    h1.classList.add('tkg-h1');
    inner.insertBefore(h1, inner.firstChild);

    // Hide the now-empty text block the theme rendered the title into, but
    // never the wrapper that also holds the guide content.
    for (var hops = 0; hops < 3 && holder; hops++) {
      if (
        holder.classList.contains('section-content-wrapper') ||
        holder.classList.contains('page-content') ||
        holder.contains(root) ||
        holder.textContent.trim() !== '' ||
        holder.querySelector('img, svg, video, iframe')
      ) {
        break;
      }
      holder.classList.add('tkg-hidden');
      holder = holder.parentElement;
    }
  }

  /* ---------------------------------------------------------------- 3 */
  function buildShell(hero) {
    var body = doc.createElement('div');
    body.className = 'tkg-body';

    var rail = doc.createElement('aside');
    rail.className = 'tkg-rail';

    var main = doc.createElement('div');
    main.className = 'tkg-main';

    body.appendChild(rail);
    body.appendChild(main);

    var kids = Array.prototype.slice.call(root.children);
    root.appendChild(body);

    var cta = null;
    kids.forEach(function (el) {
      if (el === hero || el === body) return;
      if (el.classList && el.classList.contains('tkg-cta')) {
        cta = el;
        return;
      }
      main.appendChild(el);
    });

    // The closing CTA runs edge to edge under the reading column.
    if (cta) root.appendChild(cta);

    // The "In this guide" block becomes the standfirst once the chips move.
    var chips = main.querySelector('.tkg-chips');
    if (chips) {
      var sec = chips.closest('.tkg-sec');
      if (sec) sec.classList.add('tkg-intro');
    }

    return { body: body, rail: rail, main: main };
  }

  /* ---------------------------------------------------------------- 4 */
  // Prefer the page's own jump chips; fall back to the section headings so a
  // guide without a chip row still gets a contents rail.
  function tocEntries() {
    var out = [];
    var chips = root.querySelector('.tkg-chips');

    if (chips) {
      Array.prototype.forEach.call(chips.querySelectorAll('a'), function (a) {
        var href = a.getAttribute('href') || '';
        if (href.charAt(0) !== '#') return;
        var target = doc.getElementById(href.slice(1));
        if (target) out.push({ label: (a.textContent || '').trim(), href: href, target: target });
      });
      if (out.length) return out;
    }

    Array.prototype.forEach.call(root.querySelectorAll('.tkg-main .tkg-sec[id]'), function (sec) {
      var eyebrow = sec.querySelector('.tkg-eyebrow');
      var heading = sec.querySelector('h2');
      var label = ((eyebrow && eyebrow.textContent) || (heading && heading.textContent) || '').trim();
      if (!label) return;
      if (label.length > 34) label = label.slice(0, 32).replace(/\s+\S*$/, '') + '…';
      out.push({ label: label, href: '#' + sec.id, target: sec });
    });

    return out;
  }

  function buildRail(rail) {
    var links = [];
    var entries = tocEntries();
    if (!entries.length) return links;

    var list = doc.createElement('ul');

    entries.forEach(function (entry) {
      var li = doc.createElement('li');
      var link = doc.createElement('a');
      link.setAttribute('href', entry.href);
      link.textContent = entry.label;
      li.appendChild(link);
      list.appendChild(li);
      links.push({ li: li, target: entry.target });
    });

    var title = doc.createElement('p');
    title.className = 'tkg-rail-title';
    title.textContent = 'In this guide';

    var nav = doc.createElement('nav');
    nav.className = 'tkg-rail-nav';
    nav.setAttribute('aria-label', 'Sections of this guide');
    nav.appendChild(list);

    rail.appendChild(title);
    rail.appendChild(nav);

    var chips = root.querySelector('.tkg-chips');
    if (chips) chips.classList.add('tkg-hidden');

    // Carry the shop's phone number into the rail.
    var tel = root.querySelector('a[href^="tel:"]');
    if (tel) {
      var card = doc.createElement('div');
      card.className = 'tkg-rail-card';

      var label = doc.createElement('span');
      label.textContent = 'Questions?';

      var call = doc.createElement('a');
      call.setAttribute('href', tel.getAttribute('href'));
      call.textContent = (tel.textContent || '').replace(/^\s*call or text\s*/i, '').trim();

      var note = doc.createElement('em');
      note.textContent = 'Call or text the shop';

      card.appendChild(label);
      card.appendChild(call);
      card.appendChild(note);
      rail.appendChild(card);
    }

    return links;
  }

  /* ---------------------------------------------------------------- 5 */
  // Only a header that actually stays on screen needs to be cleared. Horizon's
  // header is static unless the sticky-header setting is on, so this returns 0
  // in that case and the rail sits right at the top edge.
  function headerHeight() {
    var el = doc.querySelector('#header-component');
    while (el && el !== doc.body) {
      var pos = window.getComputedStyle(el).position;
      if (pos === 'sticky' || pos === 'fixed') return el.getBoundingClientRect().height;
      el = el.parentElement;
    }
    return 0;
  }

  function railBarHeight() {
    if (window.innerWidth > 1023) return 0;
    var rail = root.querySelector('.tkg-rail');
    return rail ? rail.getBoundingClientRect().height : 0;
  }

  function scrollOffset() {
    return headerHeight() + railBarHeight() + 24;
  }

  function setOffset() {
    doc.body.style.setProperty('--tkg-hdr', headerHeight() + 'px');
    doc.body.style.setProperty('--tkg-offset', scrollOffset() + 'px');
  }

  /* ---------------------------------------------------------------- 6 */
  // Anchor jumps stay native — the browser handles them in whichever element
  // scrolls, and `scroll-margin-top` on the sections clears the sticky bar.
  function applySmoothScroll() {
    if (!prefersMotion()) return;
    (scroller || doc.documentElement).style.scrollBehavior = 'smooth';
  }

  function wireSpy(links) {
    if (!links.length) return;
    var active = -1;

    function update() {
      var top = scrollTop();
      var line = top + scrollOffset() + 12;
      var next = 0;
      for (var i = 0; i < links.length; i++) {
        if (offsetTopIn(links[i].target) <= line) next = i;
      }
      // Pin the last item once the end of the page is reached.
      if (top > 0 && viewportH() + top >= contentH() - 80) {
        next = links.length - 1;
      }
      if (next === active) return;
      if (active > -1) links[active].li.classList.remove('is-active');
      links[next].li.classList.add('is-active');
      active = next;
      keepInView(links[next].li);
    }

    onScroll(update);
    window.addEventListener('load', update);
    window.addEventListener('resize', debounce(update, 150));
    update();
  }

  function keepInView(li) {
    if (window.innerWidth > 1023) return;
    var list = li.parentElement;
    if (!list || list.scrollWidth <= list.clientWidth) return;
    var left = li.offsetLeft - list.clientWidth / 2 + li.offsetWidth / 2;
    if (list.scrollTo) {
      list.scrollTo({ left: Math.max(0, left), behavior: prefersMotion() ? 'smooth' : 'auto' });
    } else {
      list.scrollLeft = Math.max(0, left);
    }
  }

  function wireProgress() {
    var bar = doc.createElement('div');
    bar.className = 'tkg-progress';
    bar.setAttribute('aria-hidden', 'true');
    doc.body.appendChild(bar);

    onScroll(function () {
      var start = offsetTopIn(root);
      var total = root.getBoundingClientRect().height - viewportH();
      var done = total > 0 ? (scrollTop() - start) / total : 0;
      bar.style.transform = 'scaleX(' + Math.min(1, Math.max(0, done)) + ')';
    });
  }

  function wireBackToTop() {
    var btn = doc.createElement('button');
    btn.type = 'button';
    btn.className = 'tkg-totop';
    btn.setAttribute('aria-label', 'Back to top');
    btn.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
      '<path d="M8 13V3M3.5 7.5L8 3l4.5 4.5" fill="none" stroke="currentColor" stroke-width="1.6" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>';
    doc.body.appendChild(btn);

    btn.addEventListener('click', function () {
      (scroller || window).scrollTo({ top: 0, behavior: prefersMotion() ? 'smooth' : 'auto' });
    });

    onScroll(function () {
      btn.classList.toggle('is-on', scrollTop() > 900);
    });
  }

  /* ---------------------------------------------------------------- 7 */
  function wireReveal() {
    if (!('IntersectionObserver' in window)) return;

    var items = root.querySelectorAll(
      '.tkg-card, .tkg-cut, .tkg-steps li, .tkg-stats div, .tkg-tablewrap, .tkg-split > img, .tkg-note'
    );
    var meters = root.querySelectorAll('.tkg-meter');

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    Array.prototype.forEach.call(items, function (el, i) {
      el.classList.add('tkg-reveal');
      var group = el.parentElement;
      var index = group ? Array.prototype.indexOf.call(group.children, el) : i;
      el.style.transitionDelay = Math.min(index, 5) * 70 + 'ms';
      io.observe(el);
    });

    Array.prototype.forEach.call(meters, function (el) {
      io.observe(el);
    });

    // Safety net: never leave content hidden if something goes wrong.
    window.setTimeout(function () {
      Array.prototype.forEach.call(items, function (el) {
        el.classList.add('is-in');
      });
      Array.prototype.forEach.call(meters, function (el) {
        el.classList.add('is-in');
      });
    }, 4000);
  }

  /* --------------------------------------------------------------- utils */
  function prefersMotion() {
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function onScroll(fn) {
    handlers.push(fn);
    attachScrollHost();
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      window.clearTimeout(t);
      t = window.setTimeout(fn, wait);
    };
  }
})();
